import { formatCnpj } from '@dnx/domain';
import { Alert, Badge, Button, Card, CardHeader, Switch, Tabs } from '@dnx/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, Pause, Play } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { portalUrl } from '../lib/host.js';
import { BrandEditor } from './brand-editor.js';
import { WhatsappTab } from './whatsapp-tab.js';
import { OrgAvatar } from './org-avatar.js';
import { fmtDate, orgQuery, planLabel, STATUS_LABEL } from './queries.js';
import { TeamTab } from './team-tab.js';

export type DetailTab = 'geral' | 'equipe' | 'marca' | 'whatsapp';
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const route = getRouteApi('/app-platform/clinicas/$id');

function Overview({ org }: { org: ReturnType<typeof useOrg> }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const suspended = org.status === 'suspended';
  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended') => api<void>(`/platform/organizations/${org.id}/status`, { method: 'PATCH', body: { status } }),
    onSuccess: async () => { setConfirming(false); await qc.invalidateQueries({ queryKey: ['platform', 'orgs'] }); },
  });
  const rows: [string, string][] = [
    ['Razão social', org.legalName],
    ['CNPJ', formatCnpj(org.cnpj)],
    ['Plano', planLabel(org.plan)],
    ['Criada em', fmtDate(org.createdAt, true)],
    ['Pacientes cadastrados', String(org.contactsCount)],
  ];
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader title="Dados da clínica" />
        <dl className="divide-y divide-border px-5 py-2 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 py-2.5"><dt className="text-fg-muted">{k}</dt><dd className="text-right font-medium text-fg">{v}</dd></div>
          ))}
        </dl>
      </Card>
      <div className="grid gap-5">
        <Card>
          <CardHeader title="Endereços" description="O subdomínio da plataforma fica sempre ativo, mesmo com domínio próprio." />
          <ul className="space-y-2 p-5">
            {org.domains.map((d) => (
              <li key={d.domain} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
                <a href={portalUrl(d.domain)} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-1.5 font-mono text-[12.5px] text-fg hover:text-primary">
                  <span className="truncate">{d.domain}</span><ExternalLink className="size-3.5 shrink-0" aria-hidden />
                </a>
                <span className="flex shrink-0 gap-1.5">
                  {d.isPrimary && <Badge tone="primary">Principal</Badge>}
                  <Badge tone={d.status === 'active' ? 'success' : 'warning'}>{d.status === 'active' ? 'Ativo' : d.status}</Badge>
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <AiCard org={org} />
        {org.status === 'onboarding' && (
          <Card>
            <CardHeader title="Em implantação" description="Quando a equipe estiver configurada e o piloto começar, marque a clínica como ativa." />
            <div className="p-5">
              <Button loading={setStatus.isPending} onClick={() => setStatus.mutate('active')}><Play className="size-4" />Marcar como ativa</Button>
            </div>
          </Card>
        )}
        <Card>
          <CardHeader title={suspended ? 'Clínica suspensa' : 'Suspender clínica'}
            description={suspended ? 'Portal e painel desta clínica estão fora do ar.' : 'Tira do ar o portal dos pacientes e o painel da equipe imediatamente. Nenhum dado é apagado.'} />
          <div className="p-5">
            {suspended ? (
              <Button variant="secondary" loading={setStatus.isPending} onClick={() => setStatus.mutate('active')}><Play className="size-4" />Reativar clínica</Button>
            ) : !confirming ? (
              <Button variant="secondary" onClick={() => setConfirming(true)}><Pause className="size-4" />Suspender…</Button>
            ) : (
              <Alert tone="danger" title={`Suspender ${org.tradeName}?`}>
                <p>{plural(org.contactsCount, 'paciente', 'pacientes')} e {plural(org.membersCount, 'pessoa', 'pessoas')} da equipe perdem o acesso agora. Você pode reativar depois.</p>
                <div className="mt-3 flex gap-2">
                  <Button variant="danger" size="sm" loading={setStatus.isPending} onClick={() => setStatus.mutate('suspended')}>Suspender agora</Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Cancelar</Button>
                </div>
              </Alert>
            )}
            {setStatus.error && <Alert tone="danger" className="mt-3">{setStatus.error.message}</Alert>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function AiCard({ org }: { org: ReturnType<typeof useOrg> }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api<void>(`/platform/organizations/${org.id}/ai`, { method: 'PUT', body: { enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'orgs'] }),
  });
  return (
    <Card>
      <CardHeader title="Assistente virtual" description="Responde dúvidas gerais e agenda avaliações no WhatsApp, só com a base de conhecimento aprovada pelos médicos da clínica." />
      <div className="p-5">
        <Switch label={org.aiEnabled ? 'Ligado' : 'Desligado'} description="Desligado: todas as conversas vão direto para a equipe (kill switch)."
          checked={org.aiEnabled} disabled={toggle.isPending} onChange={(v) => toggle.mutate(v)} />
        {toggle.error && <Alert tone="danger" className="mt-3">{toggle.error.message}</Alert>}
      </div>
    </Card>
  );
}

function useOrg() {
  const { id } = route.useParams();
  return useSuspenseQuery(orgQuery(id)).data;
}

export function OrganizationDetailPage() {
  const org = useOrg();
  const qc = useQueryClient();
  const { aba } = route.useSearch();
  const navigate = useNavigate();
  const s = STATUS_LABEL[org.status]!;
  const tab: DetailTab = aba ?? 'geral';

  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/clinicas" className="-ml-2 inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4" />Clínicas</Link>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <OrgAvatar name={org.tradeName} color={org.branding.colorPrimary} size="lg" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight text-fg">{org.tradeName}</h1>
              <Badge tone={s.tone}>{s.label}</Badge>
            </div>
            <p className="mt-0.5 font-mono text-[12.5px] text-fg-muted">{org.portalHost}</p>
          </div>
        </div>
        <a href={portalUrl(org.portalHost)} target="_blank" rel="noreferrer">
          <Button variant="secondary"><ExternalLink className="size-4" />Abrir portal</Button>
        </a>
      </div>

      <Tabs className="mt-6" value={tab}
        onChange={(v) => void navigate({ to: '/clinicas/$id', params: { id: org.id }, search: { aba: v }, replace: true })}
        tabs={[{ value: 'geral', label: 'Visão geral' }, { value: 'equipe', label: 'Equipe', count: org.members.length }, { value: 'marca', label: 'Marca' }, { value: 'whatsapp', label: 'WhatsApp' }]} />
      <div className="mt-5" role="tabpanel">
        {tab === 'geral' && <Overview org={org} />}
        {tab === 'whatsapp' && <WhatsappTab orgId={org.id} />}
        {tab === 'equipe' && <TeamTab org={org} />}
        {tab === 'marca' && <BrandEditor key={org.id} endpoint={`/platform/organizations/${org.id}/branding`} initial={org.branding}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['platform', 'orgs'] })} />}
      </div>
    </div>
  );
}
